import { useCallback, useEffect, useState } from 'react';

/**
 * How long the dialog waits for the action to report busy. A request that never reports busy
 * (it failed at once, or the caller has no request) still closes the dialog after this time.
 */
export const CONFIRM_START_TIMEOUT_MS = 1000;

type ConfirmPhase = 'closed' | 'open' | 'started' | 'running';

interface UseConfirmWhileBusyArgs {
  /** The busy flag of the request that the action starts (for example a mutation's isLoading). */
  isBusy: boolean;
  /** Starts the action. The hook does not wait for a promise: it watches `isBusy`. */
  onConfirm: () => void;
}

/**
 * Keeps a confirm dialog open while the action it started runs (design dialogs a4), for a caller
 * that knows only a busy flag. Confirm starts the action; the dialog stays open while `isBusy`
 * is true and closes when it turns false again. Cancel does nothing while the action runs.
 */
export function useConfirmWhileBusy({ isBusy, onConfirm }: UseConfirmWhileBusyArgs) {
  const [phase, setPhase] = useState<ConfirmPhase>('closed');

  useEffect(() => {
    if (phase === 'started' && isBusy) {
      setPhase('running');
      return undefined;
    }
    if (phase === 'running' && !isBusy) {
      setPhase('closed');
      return undefined;
    }
    if (phase === 'started') {
      const timer = setTimeout(() => setPhase('closed'), CONFIRM_START_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isBusy, phase]);

  const open = useCallback(() => setPhase('open'), []);
  const cancel = useCallback(
    () => setPhase((current) => (current === 'started' || current === 'running' ? current : 'closed')),
    []
  );
  const confirm = useCallback(() => {
    setPhase('started');
    onConfirm();
  }, [onConfirm]);

  return {
    isOpen: phase !== 'closed',
    /** True from the click until the action ends: show it as `isBusy` on the dialog. */
    isRunning: phase === 'started' || phase === 'running',
    open,
    cancel,
    confirm,
  };
}
